import { Dialog as DialogPrimitive } from 'radix-ui';
import {
  type FormEvent,
  type MouseEventHandler,
  ReactNode,
  useMemo,
} from 'react';

import { BaseModal, type IBaseModalProps } from '../BaseModal';
import { BaseTooltip } from '../BaseTooltip';
import { FormBase } from '../Form';
import {
  AdditionalText,
  Button,
  ButtonsContainer,
  ErrorMessage,
  ErrorMessageContainer,
  TextContent,
  Title,
} from './styles';

export interface IFormModalProps extends Partial<IBaseModalProps> {
  acceptText?: string;
  dataCy?: string;
  declineText?: string;
  disabled?: boolean;
  errorMessage?: string;
  loading?: boolean;
  mainText?: string;
  formId?: string;
  acceptButtonTooltipText?: string | null;
  tooltipText?: string;
  onAccept?: (event: FormEvent<HTMLFormElement>) => Promise<void> | void;
  onDecline?: MouseEventHandler<HTMLButtonElement>;
  themeStyle?: 'admin' | 'default';
  customButtons?: ReactNode;
  additionalText?: string;
}

export const FormModal = ({
  acceptText,
  children,
  dataCy = 'form-modal',
  declineText,
  disabled = false,
  errorMessage,
  loading = false,
  mainText = '',
  acceptButtonTooltipText = null,
  tooltipText,
  formId,
  onAccept,
  onDecline,
  themeStyle = 'default',
  customButtons,
  additionalText,
  ...props
}: IFormModalProps): JSX.Element => {
  const errors = useMemo<string[]>(() => {
    if (!errorMessage) return [];

    return errorMessage.split('\n');
  }, [errorMessage]);

  return (
    <BaseModal loading={loading} dataCy={dataCy} {...props}>
      {mainText !== '' && (
        <TextContent data-cy="text-container">
          <DialogPrimitive.Title asChild>
            {tooltipText ? (
              <BaseTooltip message={tooltipText}>
                <Title themeStyle={themeStyle} data-cy={`title-${dataCy}`}>
                  {mainText}
                </Title>
              </BaseTooltip>
            ) : (
              <Title themeStyle={themeStyle} data-cy={`title-${dataCy}`}>
                {mainText}
              </Title>
            )}
          </DialogPrimitive.Title>
          {additionalText && (
            <DialogPrimitive.Description asChild>
              <AdditionalText
                themeStyle={themeStyle}
                data-cy={`additional-text-${dataCy}`}
              >
                {additionalText}
              </AdditionalText>
            </DialogPrimitive.Description>
          )}
        </TextContent>
      )}
      <FormBase.Root id={formId} onSubmit={onAccept}>
        {children}
        {errorMessage && (
          <ErrorMessageContainer data-cy="error-message">
            {errors.map((error) => (
              <ErrorMessage key={error}>{error}</ErrorMessage>
            ))}
          </ErrorMessageContainer>
        )}
        <FormBase.Content>
          <ButtonsContainer>
            {customButtons}
            {!customButtons && declineText && (
              <Button
                type="button"
                dataCy="button-cancel"
                styleType="outline"
                onClick={onDecline}
                disabled={loading}
              >
                {declineText}
              </Button>
            )}
            {!customButtons &&
              acceptText &&
              (acceptButtonTooltipText ? (
                <BaseTooltip message={acceptButtonTooltipText}>
                  <Button
                    type="submit"
                    dataCy="button-submit"
                    loading={loading}
                    disabled={disabled}
                  >
                    {acceptText}
                  </Button>
                </BaseTooltip>
              ) : (
                <Button
                  type="submit"
                  dataCy="button-submit"
                  loading={loading}
                  disabled={disabled}
                >
                  {acceptText}
                </Button>
              ))}
          </ButtonsContainer>
        </FormBase.Content>
      </FormBase.Root>
    </BaseModal>
  );
};
